/**
 * CalibrationDetailsPanel - "Strength vs Maturity Details" (top left of the
 * calibration page). Creates a calibration, or edits a draft one:
 *
 *   Name, Type (System / Manual), Maturity equation, Concrete start,
 *   Pour linked to (+ pour code), Specimen 1 / 2 sensor (+ up to 4).
 *
 * System: the sensor cylinders are monitored as a pour; each test result's
 * maturity is read from them (ASTM C1074 8.2, 8.5). Manual: maturity is typed
 * from an outside meter, so the user says which maturity equation it used.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Lock as LockIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import calibrationsService, {
  Calibration,
  CalibrationPourOption,
} from '../../services/calibrations';
import maturityService, { ArrheniusTarget, NurseSaulTarget } from '../../services/maturity';
import { apiErrorText } from '../../utils/apiError';
import {
  apiLocalToInput,
  describeBasis,
  isSystemType,
  toLocalInputValue,
  utcNaiveToDate,
} from './calibrationFormat';

const MAX_SPECIMENS = 4;

interface Props {
  /** null = create mode. */
  calibration: Calibration | null;
  companyId?: string;
  productId?: string;
  /** Mix design's maturity equation, suggested for a new Manual calibration. */
  productEquationId?: string;
  canEdit: boolean;
  hasResults: boolean;
  onCreated?: (cal: Calibration) => void;
  onSaved?: (cal: Calibration) => void;
  onError: (message: string) => void;
}

interface Equation {
  id: string;
  label: string;
}

// One label-left row, as in the mockup.
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '150px minmax(0, 1fr)', alignItems: 'center', columnGap: 2, py: 0.6 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);
const loggerText = (s: { logger_code?: string | null; channel_no?: number | null; name: string }) =>
  `${s.logger_code || 'Logger'} ch ${s.channel_no ?? ''} · ${s.name}`;

const CalibrationDetailsPanel: React.FC<Props> = ({
  calibration, companyId, productId, productEquationId,
  canEdit, hasResults, onCreated, onSaved, onError,
}) => {
  const creating = calibration == null;
  const locked = !!calibration?.is_locked;
  const editable = canEdit && !locked;
  const now = useMemo(() => new Date(), []);

  const initialSpecimens = (cal: Calibration | null): string[] => {
    const ids = (cal?.specimens || []).sort((a, b) => a.slot - b.slot).map((s) => s.location_id);
    while (ids.length < 2) ids.push('');
    return ids;
  };

  const [name, setName] = useState(calibration?.name || '');
  const [type, setType] = useState<string>(calibration?.calibration_type || 's');
  const [start, setStart] = useState(apiLocalToInput(calibration?.batched_time) || toLocalInputValue(now));
  const [equationId, setEquationId] = useState(calibration?.maturity_equation_id || '');
  const [pourId, setPourId] = useState(calibration?.pour_id || '');
  const [specimens, setSpecimens] = useState<string[]>(initialSpecimens(calibration));
  const [pourOptions, setPourOptions] = useState<CalibrationPourOption[]>([]);
  const [equations, setEquations] = useState<Equation[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Reset when another calibration is shown (new version, reload).
  useEffect(() => {
    setName(calibration?.name || '');
    setType(calibration?.calibration_type || 's');
    setStart(apiLocalToInput(calibration?.batched_time) || toLocalInputValue(now));
    setEquationId(calibration?.maturity_equation_id || '');
    setPourId(calibration?.pour_id || '');
    setSpecimens(initialSpecimens(calibration));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calibration]);

  useEffect(() => {
    if (creating && !equationId && productEquationId) setEquationId(productEquationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productEquationId]);

  const system = isSystemType(type);

  useEffect(() => {
    if (!editable) return;
    let cancelled = false;
    setLoadingOptions(true);
    const work = system
      ? calibrationsService.getPourOptions().then((o) => !cancelled && setPourOptions(o))
      : Promise.all([
          maturityService.listNurseSaul(companyId).catch(() => [] as NurseSaulTarget[]),
          maturityService.listArrhenius(companyId).catch(() => [] as ArrheniusTarget[]),
        ]).then(([ns, ar]) => {
          if (cancelled) return;
          setEquations([
            ...ns.map((n) => ({
              id: n.id,
              label: `${n.group_id}${n.group_description ? ` (${n.group_description})` : ''}: Nurse-Saul, datum ${n.datum_temp} °C`,
            })),
            ...ar.map((a) => ({
              id: a.id,
              label: `${a.group_id}${a.group_description ? ` (${a.group_description})` : ''}: Arrhenius, Q ${a.q} K, ${a.specified_temp} °C`,
            })),
          ]);
        });
    work
      .catch((e) => !cancelled && onError(apiErrorText(e, 'The options could not be loaded')))
      .finally(() => !cancelled && setLoadingOptions(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [system, editable, companyId]);

  const selectedPour = pourOptions.find((p) => p.pour_id === pourId);
  const chosenSpecimens = specimens.filter(Boolean);

  const pourStartLocal = (p?: CalibrationPourOption | null): string | null =>
    p?.started ? toLocalInputValue(utcNaiveToDate(p.started)) : null;

  const handlePour = (id: string) => {
    setPourId(id);
    setSpecimens(['', '']);
    const p = pourOptions.find((o) => o.pour_id === id);
    const s = pourStartLocal(p);
    if (s) setStart(s);
  };

  const setSpecimen = (index: number, value: string) =>
    setSpecimens((prev) => prev.map((v, i) => (i === index ? value : v)));

  const savedSpecimens = initialSpecimens(calibration).filter(Boolean);
  const pourDirty =
    system && ((calibration?.pour_id || '') !== pourId || savedSpecimens.join('|') !== chosenSpecimens.join('|'));
  const fieldsDirty =
    !creating &&
    ((calibration?.name || '') !== name ||
      (calibration?.calibration_type || 's') !== type ||
      apiLocalToInput(calibration?.batched_time) !== start ||
      (!system && calibration?.supports_basis !== false && (calibration?.maturity_equation_id || '') !== equationId));
  const dirty = creating || pourDirty || fieldsDirty;

  const errors = {
    name: !name.trim(),
    start: !start,
    duplicate: new Set(chosenSpecimens).size !== chosenSpecimens.length,
    specimens: system && !!pourId && chosenSpecimens.length === 0,
  };
  const invalid = errors.name || errors.start || errors.duplicate || errors.specimens;

  const handleSave = async () => {
    setSubmitted(true);
    if (invalid) return;
    setSaving(true);
    try {
      let cal: Calibration;
      if (creating) {
        cal = await calibrationsService.create({
          name: name.trim(),
          company_id: companyId,
          calibration_type: type,
          batched_time: start,
          sampled_time: start,
          product_id: productId || undefined,
          maturity_equation_id: !system && equationId ? equationId : undefined,
        } as any);
      } else {
        cal = calibration as Calibration;
        if (fieldsDirty) {
          cal = await calibrationsService.update(cal.id, {
            name: name.trim(),
            calibration_type: type,
            batched_time: start,
            ...(!system && cal.supports_basis !== false ? { maturity_equation_id: equationId } : {}),
          } as any);
        }
      }
      if (system && (creating ? !!pourId : pourDirty)) {
        cal = await calibrationsService.setCalibrationPour(cal.id, pourId, chosenSpecimens);
      }
      if (creating) onCreated?.(cal);
      else onSaved?.(cal);
      setSubmitted(false);
    } catch (e) {
      onError(apiErrorText(e, creating ? 'The calibration could not be created' : 'The calibration could not be saved'));
    } finally {
      setSaving(false);
    }
  };

  const pourLink = (p?: Calibration['pour']) => p?.pour_id ? (
    <Link component={RouterLink} to={`/pours/${p.pour_id}`} variant="body2">
      {p.pour_name || p.pour_id}{p.pour_code ? ` (${p.pour_code})` : ''}
    </Link>
  ) : <Typography variant="body2">-</Typography>;

  // ---------------------------------------------------------------- read-only
  if (!editable && !creating) {
    const cal = calibration as Calibration;
    return (
      <Box>
        <Row label="Calibration name"><Typography variant="body2">{cal.name || '-'}</Typography></Row>
        <Row label="Calibration type"><Typography variant="body2">{system ? 'System' : 'Manual'}</Typography></Row>
        {system ? (
          <>
            <Row label="Pour linked to">{pourLink(cal.pour)}</Row>
            {(cal.specimens || []).map((s) => (
              <Row key={s.slot} label={`Specimen ${s.slot} logger`}>
                <Typography variant="body2">{loggerText(s)}{s.accepted ? '' : ' (not counted)'}</Typography>
              </Row>
            ))}
          </>
        ) : (
          <Row label="Maturity equation"><Typography variant="body2">{describeBasis(cal.basis)}</Typography></Row>
        )}
        <Row label="Logging start time">
          <Typography variant="body2">{cal.batched_time ? new Date(cal.batched_time).toLocaleString() : '-'}</Typography>
        </Row>
        {locked && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <LockIcon fontSize="inherit" />
            {cal.used_by_pours
              ? `Used by ${cal.used_by_pours} pour${cal.used_by_pours === 1 ? '' : 's'}. Make a new version to change it.`
              : 'Finished. Make a new version to change it.'}
          </Typography>
        )}
      </Box>
    );
  }

  // ---------------------------------------------------------------- editable
  const errorText = !submitted ? '' : errors.name ? 'Give the calibration a name.'
    : errors.start ? 'Enter the logging start time.'
    : errors.specimens ? 'Choose at least one logger.'
    : errors.duplicate ? 'Each specimen needs a different logger.' : '';
  const fixed = !!calibration && hasResults;

  return (
    <Box>
      <Row label="Calibration name">
        <TextField size="small" fullWidth value={name} onChange={(e) => setName(e.target.value)}
          error={submitted && errors.name} inputProps={{ maxLength: 50, 'aria-label': 'calibration name' }} />
      </Row>
      <Row label="Calibration type">
        <Tooltip title={hasResults ? 'Delete the test results to change the type' : ''}>
          <Select size="small" fullWidth value={type} disabled={hasResults}
            onChange={(e) => setType(String(e.target.value))} inputProps={{ 'aria-label': 'calibration type' }}>
            <MenuItem value="s">System (ConXedge loggers)</MenuItem>
            <MenuItem value="m">Manual (maturity typed in)</MenuItem>
          </Select>
        </Tooltip>
      </Row>

      {!system && (
        <Row label="Maturity equation">
          {calibration && calibration.supports_basis === false ? (
            <Typography variant="body2" color="text.secondary">{describeBasis(calibration.basis)}</Typography>
          ) : (
            <Select size="small" fullWidth displayEmpty
              value={equations.some((e) => e.id === equationId) ? equationId : ''}
              onChange={(e) => setEquationId(String(e.target.value))} inputProps={{ 'aria-label': 'maturity equation' }}>
              <MenuItem value=""><em>Not chosen</em></MenuItem>
              {equations.map((e) => <MenuItem key={e.id} value={e.id}>{e.label}</MenuItem>)}
            </Select>
          )}
        </Row>
      )}

      {system && (fixed ? (
        <>
          <Row label="Pour linked to">{pourLink(calibration?.pour)}</Row>
          {(calibration?.specimens || []).map((s) => (
            <Row key={s.slot} label={`Specimen ${s.slot} logger`}>
              <Tooltip title="Fixed while there are test results, because their maturity came from this logger">
                <Typography variant="body2">{loggerText(s)}{s.accepted ? '' : ' (not counted)'}</Typography>
              </Tooltip>
            </Row>
          ))}
        </>
      ) : (
        <>
          <Row label="Pour linked to">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Select size="small" fullWidth displayEmpty disabled={loadingOptions}
                value={pourOptions.some((p) => p.pour_id === pourId) ? pourId : ''}
                onChange={(e) => handlePour(String(e.target.value))} inputProps={{ 'aria-label': 'pour linked to' }}>
                <MenuItem value=""><em>{loadingOptions ? 'Loading pours' : 'Choose the pour'}</em></MenuItem>
                {pourOptions.map((p) => (
                  <MenuItem key={p.pour_id} value={p.pour_id}>{p.pour_name}{p.pour_code ? ` (${p.pour_code})` : ''}</MenuItem>
                ))}
              </Select>
              {loadingOptions && <CircularProgress size={18} />}
            </Box>
          </Row>
          {specimens.map((value, i) => {
            const taken = new Set(specimens.filter((v, j) => v && j !== i));
            return (
              <Row key={i} label={`Specimen ${i + 1} logger`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Select size="small" fullWidth displayEmpty disabled={!selectedPour}
                    error={submitted && i === 0 && (errors.specimens || errors.duplicate)}
                    value={selectedPour?.locations.some((l) => l.id === value) ? value : ''}
                    onChange={(e) => setSpecimen(i, String(e.target.value))}
                    inputProps={{ 'aria-label': `specimen ${i + 1} logger` }}>
                    <MenuItem value=""><em>{selectedPour ? 'Choose a logger' : 'Choose the pour first'}</em></MenuItem>
                    {(selectedPour?.locations || []).map((l) => (
                      <MenuItem key={l.id} value={l.id} disabled={taken.has(l.id)}>{loggerText(l)}</MenuItem>
                    ))}
                  </Select>
                  {i >= 2 && (
                    <IconButton size="small" aria-label="remove logger"
                      onClick={() => setSpecimens((prev) => prev.filter((_, j) => j !== i))}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Row>
            );
          })}
          {selectedPour && specimens.length < MAX_SPECIMENS && (
            <Row label="">
              <Button size="small" startIcon={<AddIcon />} sx={{ textTransform: 'none' }}
                onClick={() => setSpecimens((prev) => [...prev, ''])}>
                Add logger
              </Button>
            </Row>
          )}
        </>
      ))}

      <Row label="Logging start time">
        <TextField type="datetime-local" size="small" fullWidth value={start} onChange={(e) => setStart(e.target.value)}
          error={submitted && errors.start} inputProps={{ 'aria-label': 'logging start time' }} />
      </Row>

      {(errorText || dirty) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
          <Typography variant="body2" color="error" sx={{ flexGrow: 1 }}>{errorText}</Typography>
          {dirty && (
            <Button variant="contained" size="small" onClick={handleSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}>
              {creating ? 'Create calibration' : 'Save'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CalibrationDetailsPanel;
